// ============================================
// B356 Fase 1 (AI_154) — NUMERAÇÃO DO SNCR (lógica pura)
// ============================================
// A API do SNCR entrega a numeração de Receita de Controle Especial (RCE) e de
// Receita sujeita à retenção (RET) em BLOCOS: {inicio, fim, quantidade}, ex.
// inicio "2602.6-53.0000001", fim "2602.6-53.0001000", quantidade 1000 (Manual
// da API v5, §2.3.2). Cada documento emitido consome UM número (RDC 1.000/2025,
// Art. 7º: "numeração individualizada"), e um número NUNCA se repete.
//
// Este módulo só faz aritmética — sem Firestore, sem rede — para ser testável.
// O consumo atômico mora em `cofreNumeracao.ts`.

export type TipoNumeracao = "RCE" | "RET";

export interface BlocoNumeracao {
  inicio: string;
  fim: string;
  quantidade: number;
  /** ISO — quando o bloco foi obtido no SNCR. */
  obtidoEm: string;
}

interface NumeroDecomposto {
  prefixo: string;
  sequencial: number;
  largura: number;
}

/** "2602.6-53.0000001" → { prefixo: "2602.6-53.", sequencial: 1, largura: 7 } */
export function decomporNumero(numero: string): NumeroDecomposto | null {
  const m = /^(.*?)(\d+)$/.exec((numero || "").trim());
  if (!m || !m[1]) return null;
  return { prefixo: m[1], sequencial: parseInt(m[2], 10), largura: m[2].length };
}

/**
 * Confere se o bloco devolvido pelo SNCR é coerente ANTES de guardá-lo: mesmo
 * prefixo, fim ≥ início e quantidade = fim − início + 1. Um bloco incoerente
 * gravado viraria números inválidos impressos em receita.
 */
export function validarBloco(bloco: {
  inicio: string;
  fim: string;
  quantidade: number;
}): string | null {
  const a = decomporNumero(bloco.inicio);
  const b = decomporNumero(bloco.fim);
  if (!a || !b) return "Numeração em formato inesperado.";
  if (a.prefixo !== b.prefixo || a.largura !== b.largura)
    return "Início e fim do bloco não pertencem à mesma série.";
  if (b.sequencial < a.sequencial) return "Fim do bloco anterior ao início.";
  const esperado = b.sequencial - a.sequencial + 1;
  if (!Number.isInteger(bloco.quantidade) || bloco.quantidade !== esperado)
    return `Quantidade do bloco (${bloco.quantidade}) não confere com o intervalo (${esperado}).`;
  return null;
}

/** Total de números em todos os blocos. */
export function totalNumeros(blocos: BlocoNumeracao[]): number {
  return blocos.reduce((s, b) => s + b.quantidade, 0);
}

/**
 * O n-ésimo número (0 = primeiro) percorrendo os blocos NA ORDEM em que foram
 * obtidos. Retorna null se `indice` passa do total (sem saldo).
 */
export function numeroNaPosicao(
  blocos: BlocoNumeracao[],
  indice: number
): string | null {
  if (!Number.isInteger(indice) || indice < 0) return null;
  let resto = indice;
  for (const bloco of blocos) {
    if (resto < bloco.quantidade) {
      const d = decomporNumero(bloco.inicio);
      if (!d) return null;
      return (
        d.prefixo + String(d.sequencial + resto).padStart(d.largura, "0")
      );
    }
    resto -= bloco.quantidade;
  }
  return null;
}

/** Números ainda disponíveis. */
export function saldo(blocos: BlocoNumeracao[], usados: number): number {
  return Math.max(0, totalNumeros(blocos) - usados);
}
